import React from 'react';
import {feedbackLabel} from '../ai/aiDisclosure';
import { useTranslation } from 'react-i18next';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { CritiqueFeedbackView } from 'src/hooks/useCritiqueGroups';
import {
  commentsForPart,
  FeedbackLockState,
  getFeedbackLockState,
  surveyAnswersForPart,
  userRefName,
} from 'src/utils/critiqueGroupUtil';
import { CritiqueAvatar } from './critiqueAvatar';

/**
 * Signature UX #1 — "form your own opinion first" (design cluster G).
 *
 * THE RULE THIS FILE EXISTS TO KEEP: the server decides what a member may see
 * and simply omits the rest. So this component renders only what came back, and
 * derives the lock solely from `unlockedParts` (the viewer's OWN contributions).
 * It must never imply how much feedback is hidden — under `after_own` "locked"
 * and "nobody has commented" are indistinguishable on the wire, by design, and
 * a count here would leak precisely what the gate protects.
 */

const Panel = styled.div`
  border: 1px solid #e7e3d9;
  border-radius: 14px;
  overflow: hidden;
  background: var(--br-surface);
`;

const PanelHead = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #eee7da;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const StatusPill = styled.span<{ $tone: 'locked' | 'open' }>`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${({ $tone }) => ($tone === 'open' ? '#e7f4ea' : '#f0ece3')};
  color: ${({ $tone }) => ($tone === 'open' ? '#2fa84f' : '#8a857b')};
`;

const LockBox = styled.div`
  margin: 16px;
  border: 1.5px dashed #d9d3c6;
  border-radius: 14px;
  padding: 26px 20px;
  text-align: center;
  background: var(--br-raised-surface);
  position: relative;
  overflow: hidden;
`;

// Decorative only — abstract bars, never real content. Blur is not a security
// boundary, so nothing real may ever be rendered here.
const GhostLines = styled.div`
  position: absolute;
  inset: 0;
  padding: 18px;
  opacity: 0.35;
  filter: blur(4px);
  pointer-events: none;
`;

const GhostLine = styled.div<{ $w: string }>`
  height: 12px;
  background: #d7d1c4;
  border-radius: 6px;
  width: ${({ $w }) => $w};
  margin: 6px auto;
`;

const LockIcon = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: var(--br-surface);
  border: 2px solid #f0e0cd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin: 0 auto 12px;
  box-shadow: 0 6px 16px rgba(239, 125, 34, 0.18);
`;

const LockTitle = styled.div`
  font-weight: 800;
  font-size: 15px;
  color: var(--br-text);
  position: relative;
`;

const LockBody = styled.p`
  margin: 8px auto 0;
  font-size: 13px;
  color: var(--br-muted);
  line-height: 1.5;
  max-width: 300px;
  position: relative;
`;

const HostOnlyNote = styled.div`
  margin: 16px;
  background: var(--br-raised-surface);
  border: 1px solid #eee7da;
  border-radius: 10px;
  padding: 12px;
  font-size: 12.5px;
  color: var(--br-muted);
  line-height: 1.5;
  text-align: center;
`;

const FeedbackCard = styled.div`
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 12px;
  padding: 13px 15px;
  margin-bottom: 12px;
`;

const CardHead = styled.div`
  display: flex;
  gap: 9px;
  align-items: center;
`;

const CardText = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--br-text);
  line-height: 1.5;
  white-space: pre-wrap;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 800;
  color: #8a857b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
`;

const answerToText = (answer: unknown): string => {
  if (answer === null || answer === undefined) return '';
  if (typeof answer === 'string') return answer;
  if (typeof answer === 'number' || typeof answer === 'boolean') {
    return String(answer);
  }
  if (Array.isArray(answer)) return answer.map(answerToText).filter(Boolean).join(', ');
  return '';
};

type Props = {
  view?: CritiqueFeedbackView;
  /** The part (chapter/survey) being viewed. */
  partId?: string;
  partLabel?: string;
  /** Rendered above the reveal — the member's own feedback + composer. */
  ownFeedback?: React.ReactNode;
  /** Where "share your thoughts" sends them (the reader). */
  onShare?: () => void;
};

export const FeedbackReveal: React.FC<Props> = ({
  view,
  partId,
  partLabel,
  ownFeedback,
  onShare,
}) => {
  const { t } = useTranslation();
  const state: FeedbackLockState = getFeedbackLockState(view, partId);

  const comments = partId ? commentsForPart(view, partId) : [];
  const answers = partId ? surveyAnswersForPart(view, partId) : [];
  const revealed = [
    ...comments.map(c => ({
      key: `c-${c._id}`,
      user: c.user,
      text: c.text || '',
      aiOrigin: c.aiOrigin,
      kind: t('CritiqueKindInline', 'inline'),
    })),
    ...answers.map(a => ({
      key: `a-${a._id}`,
      user: a.user,
      text: answerToText(a.answer),
      aiOrigin: a.aiOrigin,
      kind: t('CritiqueKindSurvey', 'survey'),
    })),
  ].filter(item => item.text);

  const headerPill = () => {
    if (state === 'locked') {
      return (
        <StatusPill $tone="locked" data-testid="feedback-status-locked">
          <Icon name="lock" />
          {t('CritiqueLocked', 'Locked')}
        </StatusPill>
      );
    }
    if (state === 'unlocked' || state === 'open') {
      return (
        <StatusPill $tone="open" data-testid="feedback-status-unlocked">
          <Icon name="unlock alternate" />
          {t('CritiqueUnlocked', 'Unlocked')}
        </StatusPill>
      );
    }
    return null;
  };

  return (
    <Panel data-testid="feedback-reveal">
      <PanelHead>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>
            {partLabel || t('CritiqueThisChapter', 'This chapter')}
          </div>
          <div style={{ fontSize: 12, color: '#8a857b' }}>
            {t('CritiqueFeedbackFromCircle', 'Feedback from the circle')}
          </div>
        </div>
        {headerPill()}
      </PanelHead>

      {ownFeedback}

      {/* host_only: there is no lock to open — say where feedback goes instead. */}
      {state === 'host_only' && (
        <HostOnlyNote data-testid="feedback-host-only">
          <Icon name="user secret" style={{ color: '#8a857b' }} />
          {t(
            'CritiqueHostOnlyNote',
            'In this group, feedback goes privately to the author. You won’t see other members’ comments.'
          )}
        </HostOnlyNote>
      )}

      {state === 'locked' && (
        <LockBox data-testid="feedback-locked">
          <GhostLines aria-hidden="true">
            <GhostLine $w="70%" />
            <GhostLine $w="90%" />
            <GhostLine $w="55%" />
          </GhostLines>
          <LockIcon>
            <span role="img" aria-label={t('CritiqueWriting', 'Writing')}>
              ✍️
            </span>
          </LockIcon>
          <LockTitle>
            {t('CritiqueShareToUnlock', 'Share your thoughts to unlock')}
          </LockTitle>
          {/* No count of hidden feedback — we genuinely don't know, and the
              gate exists so the member can't find out before contributing. */}
          <LockBody>
            {partLabel
              ? t(
                  'CritiqueUnlockBodyPart',
                  'Leave your own feedback on {{part}} first — then you’ll see what the rest of the circle said. Honest first, influenced never.',
                  { part: partLabel }
                )
              : t(
                  'CritiqueUnlockBody',
                  'Leave your own feedback here first — then you’ll see what the rest of the circle said. Honest first, influenced never.'
                )}
          </LockBody>
          {onShare && (
            <button
              type="button"
              onClick={onShare}
              className="ui teal button"
              style={{
                borderRadius: 9,
                fontWeight: 700,
                marginTop: 14,
                position: 'relative',
              }}
              data-testid="feedback-share-cta"
            >
              {partLabel
                ? t('CritiqueShareOnPart', 'Share my thoughts on {{part}}', {
                    part: partLabel,
                  })
                : t('CritiqueShareMine', 'Share my thoughts')}
            </button>
          )}
        </LockBox>
      )}

      {(state === 'unlocked' || state === 'open') && (
        <div style={{ padding: '16px 20px' }} data-testid="feedback-revealed">
          {revealed.length === 0 ? (
            // Unlocked but empty. Distinct from locked — the member has done
            // their part, so never ask them to contribute again here.
            <div
              style={{ fontSize: 13, color: '#8a857b', textAlign: 'center', padding: '18px 0' }}
              data-testid="feedback-revealed-empty"
            >
              <Icon name="inbox" style={{ color: '#b7b2a6' }} />{' '}
              {t(
                'CritiqueNoOthersYet',
                'No feedback from the others here yet — you’re ahead of the circle.'
              )}
            </div>
          ) : (
            <>
              <SectionLabel>
                {t('CritiqueOthersFeedback', 'From the circle')}
              </SectionLabel>
              {revealed.map(item => (
                <FeedbackCard key={item.key} data-testid="feedback-item">
                  <CardHead>
                    <CritiqueAvatar
                      id={item.user?._id || ''}
                      name={item.user?.displayName}
                      size={28}
                    />
                    <span style={{ fontWeight: 700, fontSize: 13 }}>
                      {userRefName(item.user, t)}
                    </span>
                    <span style={{ fontSize: 11, color: '#a49f93' }}>
                      · {item.kind} · {feedbackLabel(item.aiOrigin)}
                    </span>
                  </CardHead>
                  <CardText>{item.text}</CardText>
                </FeedbackCard>
              ))}
            </>
          )}
        </div>
      )}
    </Panel>
  );
};

/**
 * The celebratory beat right after a member shares (design G2). Shown only in
 * response to the member's own action — never on load, or it would read as a
 * reward for nothing.
 */
export const UnlockMoment: React.FC<{ onDone?: () => void }> = ({ onDone }) => {
  const { t } = useTranslation();
  return (
    <div
      data-testid="unlock-moment"
      style={{
        margin: 16,
        borderRadius: 14,
        padding: '30px 20px',
        textAlign: 'center',
        background: 'var(--br-raised-surface)',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'var(--br-surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#2fa84f',
          fontSize: 30,
          margin: '0 auto 14px',
          boxShadow: '0 10px 26px rgba(47,168,79,.24)',
        }}
      >
        <Icon name="unlock alternate" style={{ margin: 0 }} />
      </div>
      <div style={{ fontWeight: 900, fontSize: 20 }}>
        {t('CritiqueThanksForSharing', 'Thanks for sharing!')}
      </div>
      <p
        style={{
          margin: '8px auto 16px',
          fontSize: 13.5,
          color: '#4a4740',
          lineHeight: 1.5,
          maxWidth: 290,
        }}
      >
        {t(
          'CritiqueUnlockedBody',
          'Your honest take is locked in. Here’s what the rest of the circle felt →'
        )}
      </p>
      {onDone && (
        <button
          type="button"
          className="ui teal button"
          style={{ borderRadius: 9, fontWeight: 700 }}
          onClick={onDone}
        >
          {t('CritiqueSeeTheirs', 'See what they said')}
        </button>
      )}
    </div>
  );
};
